import React, { useState } from 'react';
import { Popover, Box, TextField, Button, Stack, Typography } from '@mui/material';
import { Patient } from '../../../../types/types';
import { MuiTelInput, matchIsValidTel } from 'mui-tel-input';
import { useSnackbar } from 'notistack';
import { useAccessToken, useSubjectLanguage } from '@features/user';
import APIService from '../../../../services/APIService';
import SentryService from '../../../../services/SentryService';
import { capitalize } from '../../../../utils/utils';
import { z } from 'zod';

interface QuickPatientInfoPopoverProps {
  anchorPosition: { top: number; left: number } | null;
  open: boolean;
  onClose: () => void;
  patient: Patient;
  requiredFields: Array<'phone' | 'dob'>;
  onSave: (updatedPatient: Partial<Patient>) => void;
}

// Validation schemas
const phoneSchema = z
  .string()
  .refine(
    val => matchIsValidTel(val, { onlyCountries: ['US', 'CA', 'GB', 'AU', 'NZ', 'ZA', 'MX'] }),
    { message: 'Please enter a valid phone number' },
  );

const dobSchema = z
  .string()
  .min(1, 'Date of birth is required')
  .refine(
    val => {
      const date = new Date(val);
      const today = new Date();
      return date <= today && date > new Date('1900-01-01');
    },
    { message: 'Please enter a valid date of birth' },
  );

export const QuickPatientInfoPopover: React.FC<QuickPatientInfoPopoverProps> = ({
  anchorPosition,
  open,
  onClose,
  patient,
  requiredFields,
  onSave,
}) => {
  const [phone, setPhone] = useState(patient.phone || '');
  const [dob, setDob] = useState(patient.dob || '');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [dobError, setDobError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { getAccessToken } = useAccessToken();
  const subjectLanguage = useSubjectLanguage();

  const handlePhoneChange = (newValue: string) => {
    setPhone(newValue);
    // Clear error when user starts typing
    if (phoneError) setPhoneError(null);
  };

  const handleDobChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDob(e.target.value);
    // Clear error when user starts typing
    if (dobError) setDobError(null);
  };

  const handleSave = async () => {
    // Reset errors
    setPhoneError(null);
    setDobError(null);

    let hasErrors = false;

    // Validate phone if required
    if (requiredFields.includes('phone')) {
      const phoneResult = phoneSchema.safeParse(phone);
      if (!phoneResult.success) {
        setPhoneError(phoneResult.error.errors[0].message);
        hasErrors = true;
      }
    }

    // Validate DOB if required
    if (requiredFields.includes('dob')) {
      const dobResult = dobSchema.safeParse(dob);
      if (!dobResult.success) {
        setDobError(dobResult.error.errors[0].message);
        hasErrors = true;
      }
    }

    if (hasErrors) {
      return;
    }

    setIsSubmitting(true);

    try {
      const updates: Partial<Patient> = {};

      if (requiredFields.includes('phone') && phone) {
        updates.phone = phone;
      }

      if (requiredFields.includes('dob') && dob) {
        updates.dob = dob;
      }

      // Update via API using the same pattern as PatientInformationView
      const accessToken = await getAccessToken();
      const response = await APIService.makeAPIPostRequest({
        requestString: '/patient/update',
        accessToken,
        body: {
          patient_id: patient.patient_id,
          updated_fields: updates,
        },
      });

      if (response.ok) {
        enqueueSnackbar(`${capitalize(subjectLanguage)} information updated`, {
          variant: 'success',
        });
        // Notify parent
        onSave(updates);
        onClose();
      } else {
        throw new Error(response.error?.message || `Failed to update ${subjectLanguage}`);
      }
    } catch (error) {
      console.error('Error updating patient:', error);
      enqueueSnackbar(
        error instanceof Error ? error.message : `Failed to update ${subjectLanguage}`,
        {
          variant: 'error',
        },
      );
      SentryService.logEvent('Error updating patient quick info', {
        level: 'error',
        tags: {
          patient_id: patient.patient_id,
        },
        extra: {
          error: error,
          requiredFields: requiredFields,
        },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Popover
      open={open}
      anchorReference="anchorPosition"
      anchorPosition={anchorPosition || undefined}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      PaperProps={{
        sx: { p: 2, minWidth: 300, maxWidth: 400 },
      }}
    >
      <Stack spacing={2}>
        <Typography variant="subtitle1" fontWeight="medium">
          Add {capitalize(subjectLanguage)} Information
        </Typography>

        {requiredFields.includes('phone') && (
          <MuiTelInput
            label="Phone Number"
            value={phone}
            onChange={handlePhoneChange}
            defaultCountry="US"
            forceCallingCode
            onlyCountries={['US', 'CA', 'GB', 'AU', 'NZ', 'ZA', 'MX']}
            size="small"
            fullWidth
            autoFocus={true}
            error={!!phoneError}
            helperText={phoneError}
            sx={{
              '& input': {
                height: '1.4375em', // Match TextField height
              },
            }}
          />
        )}

        {requiredFields.includes('dob') && (
          <TextField
            label="Date of Birth"
            name="dob"
            type="date"
            value={dob}
            onChange={handleDobChange}
            variant="outlined"
            size="small"
            fullWidth
            required
            autoFocus={!requiredFields.includes('phone')}
            error={!!dobError}
            helperText={dobError}
            InputLabelProps={{ shrink: true }}
            inputProps={{
              max: new Date().toISOString().split('T')[0],
            }}
          />
        )}

        <Stack direction="row" spacing={1} justifyContent="flex-end">
          <Button size="small" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button size="small" variant="contained" onClick={handleSave} disabled={isSubmitting}>
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </Stack>
      </Stack>
    </Popover>
  );
};
